import { PrimeMemberByPublicId } from 'iam/MemberStore'
import Member from 'iam/models/Member'
import { collection } from 'infra/collection'
import { Model } from 'infra/model'
import { observable, action, computed, makeObservable } from 'mobx'
import { Id } from 'types/definitions'
import { ConversationDTO, MessageDTO, ParticipantDTO } from '../dtos/messages'
import Message from './Message'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { Participant } from './Participant'
import { filter, takeUntil, share, find, map } from 'rxjs/operators'
import { ConversationMeta } from './ConversationMeta'
import Workspace from 'iam/models/Workspace'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { MessageStatusDeltaStream } from './MessageStatusDeltaStream'
import { toStream } from 'utils/mobx-util'
import { filterNonNil } from 'utils/rx-util'
import { MessageDeliveryStatus } from './MessageStatus'
import { max } from 'date-fns'
import User from 'iam/models/User'
import { GetCurrentUser } from 'iam/SessionStore'
import { JobProgression } from 'jobs/progression/JobProgression'
import { PrimeProgression } from 'jobs/progression/JobProgressionStore'

export default class Conversation extends Model<ConversationDTO, Conversation> {
  static none = new Conversation(
    () => Workspace.none,
    () => Member.none,
    () => JobProgression.none,
    () => User.none,
  )

  active = false
  id = Id.none
  name = 'Conversation'
  dateCreated = DateZero()
  dateModified = DateZero()
  unreadCount = 0
  humanMessagesCount = 0
  workspace = Workspace.none
  meta: ConversationMeta = {
    type: 'Unknown',
  }

  /**
   * Participants collection.
   */
  participants = collection<ParticipantDTO, Participant>({
    getModelId: (m) => m.member.user.publicId,
    getDataId: (d) => d.user_public_id,
    create: () => new Participant(this, this.primeMember),
  })

  get muted() {
    const p = this.participants.find(
      (p) =>
        Id.assert(p.member.user.publicId) ===
        Id.assert(this.getCurrentUser().publicId),
    )
    if (p) {
      return p.muted
    }
    return false
  }

  /**
   * Messages collection.
   */
  messages = collection<MessageDTO, Message>({
    create: () => {
      const message = new Message(
        this,
        this.primeMember,
        this.primeProgressionStep,
      )

      // An observable that emits a value and completes when the
      // message is removed from the collection.
      const messageRemoved$ = this.messages.removals$.pipe(
        find((m) => m === message),
        share(),
      )

      // Whenever we see a timestamp on the message that is newer than that of the
      // conversation, update the one on the conversation.
      toStream(() => max([message.dateCreated, message.dateModified]), true)
        .pipe(
          map((date) => max([date, this.dateModified])),
          takeUntil(messageRemoved$),
        )
        .subscribe(
          action((date: Date) => {
            this.dateModified = date
          }),
        )

      // Status changes pertaining to the current user.
      const myStatusChanges$ = message.statusChanges$.pipe(
        filter((status) => status.member.user.isCurrentUser),
      )

      // Once we have the CURRENT message delivery status for the current user,
      // if it's set to Read, then there's nothing left to track for that message,
      // so we need to stop the delta flow. Otherwise, a MessageDeleted event would wrongfully
      // decrement the unread count even if it was already read.
      const stop$ = toStream(
        () =>
          message.recipientStatuses.find((r) => r.member.user.isCurrentUser)
            ?.deliveryStatus,
        true,
      ).pipe(
        filterNonNil(),
        find((s) => s === MessageDeliveryStatus.Read),
      )

      // Stream of deltas used to vary the unread count.
      MessageStatusDeltaStream(myStatusChanges$, messageRemoved$)
        .pipe(takeUntil(stop$))
        .subscribe(this.varyUnreadCount)

      return message
    },
  })

  constructor(
    private primeWorkspace: PrimeWorkspace,
    private primeMember: PrimeMemberByPublicId,
    private primeProgressionStep: PrimeProgression,
    private getCurrentUser: GetCurrentUser,
  ) {
    super()
    makeObservable(this, {
      active: observable,
      id: observable,
      name: observable,
      dateCreated: observable,
      dateModified: observable,
      unreadCount: observable,
      workspace: observable,
      meta: observable,
      humanMessagesCount: observable,
      muted: computed,
      resetUnreadCount: action,
      varyUnreadCount: action.bound,
      setHumanMessagesCount: action.bound,
    })
  }

  parse(json: Partial<ConversationDTO>): Partial<Conversation> {
    // Set early to `Participant.parse` can read it.
    this.workspace =
      this.primeWorkspace({ id: json.workspace_id }) || this.workspace
    this.participants.reset(json.participants)
    return {
      id: json.id,
      active: json.active,
      name: json.name,
      meta: parseMeta(json),
      dateCreated: maybeParseDate(json.date_created),
      dateModified: maybeParseDate(json.date_modified),
    }
  }

  /**
   * Resets the unread count to the specified value.
   *
   * @param count
   */

  resetUnreadCount(count: number) {
    this.unreadCount = count
  }

  /**
   * Increases or decreases the unread count by the specified amount.
   *
   * @param delta
   */
  varyUnreadCount(delta: number) {
    this.unreadCount = Math.max(this.unreadCount + delta, 0)
  }

  /**
   * Sets the human messages count.
   *
   * @param count
   */
  setHumanMessagesCount(count: number) {
    this.humanMessagesCount = count
  }
}

/**
 * Parses conversation meta.
 */
function parseMeta({
  id,
  meta,
}: Partial<ConversationDTO>): ConversationMeta | undefined {
  if (!meta) {
    return undefined
  }

  switch (meta.type as ConversationMeta['type']) {
    case 'Job': {
      if (!meta.id) {
        console.error(
          `Attempted parsing Job conversation meta, but no ID was present. Convo ID: ${id}`,
        )
        return { type: 'Unknown' }
      }

      return { type: 'Job', name: meta.name || '<Unknown Job>', id: meta.id }
    }
    default: {
      return { type: 'Unknown' }
    }
  }
}
